import React, { useEffect, useState } from 'react';
import { useDashboard, PERIODS } from '../../hooks/useDashboard';
import type { DashboardConfig } from '../../services/api';
import type { Period } from './types';

import KpiCard         from './KpiCard';
import EnergyChart     from './EnergyChart';
import CostBreakdown   from './CostBreakdown';
import AirQualityPanel from './AirQualityPanel';
import VibrationPanel  from './VibrationPanel';
import AlertsFeed      from './AlertsFeed';
import TargetsPanel    from './TargetsPanel';

const KPI_LABELS: Record<string, string> = {
  electricity: 'Electricity',
  gas:         'Gas',
  water:       'Water',
  cost:        'Cost',
};

function LiveBadge() {
  const [time, setTime] = useState('');
  useEffect(() => {
    const fmt = () =>
      new Date().toLocaleString('en-GB', {
        weekday: 'short', day: 'numeric', month: 'short',
        hour: '2-digit', minute: '2-digit',
      });
    setTime(fmt());
    const id = setInterval(() => setTime(fmt()), 30_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <time style={styles.liveTime}>{time}</time>
      <span style={styles.liveBadge} aria-label="Live data">
        <span style={styles.liveDot} aria-hidden="true" />
        Live
      </span>
    </div>
  );
}

function LoadingState() {
  return (
    <div style={styles.stateWrap} aria-busy="true" aria-label="Loading dashboard">
      <div style={styles.spinner} />
      <p style={styles.stateText}>Loading dashboard…</p>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div style={styles.stateWrap} role="alert">
      <i className="ti ti-alert-triangle" aria-hidden="true" style={{ fontSize: 24, color: '#BA7517' }} />
      <p style={styles.stateText}>{message}</p>
      <button onClick={onRetry} style={styles.retryBtn}>Retry</button>
    </div>
  );
}

interface Props {
  osmName:       string;
  config:        DashboardConfig | null;
  siteSelector?: React.ReactNode;
}

export default function OperationalOverview({ osmName, config, siteSelector }: Props) {
  const { period, setPeriod, data, loading, error, refetch } = useDashboard({
    osmName,
    config,
  });

  return (
    <main style={styles.root}>
      <h1 className="sr-only">Skylight — Operational Overview</h1>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h3 style={styles.heading}>Overview</h3>
          <LiveBadge />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {siteSelector}
          <select
            value={period}
            onChange={e => setPeriod(e.target.value as Period)}
            style={styles.periodSelect}
            aria-label="Select reporting period"
          >
            {PERIODS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </header>

      {/* States */}
      {loading && <LoadingState />}
      {error   && !loading && <ErrorState message={error} onRetry={refetch} />}

      {/* Dashboard panels */}
      {data && !loading && (
        <>
          <div className="oo-kpi-grid" role="region" aria-label="Key performance indicators">
            {data.kpis.map(kpi => (
              <KpiCard key={kpi.id} {...kpi} label={KPI_LABELS[kpi.id] ?? kpi.id} />
            ))}
          </div>
          <div className="oo-chart-row">
            <EnergyChart   series={data.series} buckets={data.series.buckets} />
            <CostBreakdown breakdown={data.costBreakdown} />
          </div>
          <div className="oo-bottom-row">
            <AirQualityPanel airQuality={data.airQuality} />
            <VibrationPanel  vibration={data.vibration} />
            <AlertsFeed      alerts={data.alerts} />
            <TargetsPanel    comparison={data.comparison} prevPeriodLabel={data.prevPeriodLabel} />
          </div>
        </>
      )}
    </main>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    fontFamily: "'Syne', sans-serif",
    padding: '1.5rem 1.5rem 2.5rem',
    width: '75vw',
    margin: '0 auto',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  heading: {
    fontSize: 20,
    fontWeight: 600,
    margin: 0,
    color: 'var(--color-text-primary, #2C2C2A)',
  },
  liveTime: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 12,
    color: 'var(--color-text-secondary, #5F5E5A)',
  },
  liveBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: '#E1F5EE',
    color: '#0F6E56',
    fontSize: 11,
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: 20,
    fontFamily: "'DM Mono', monospace",
  },
  liveDot: {
    display: 'inline-block',
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#1D9E75',
    animation: 'skylight-pulse 2s infinite',
  },
  periodSelect: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 12,
    padding: '5px 10px',
    borderRadius: 8,
    border: '0.5px solid var(--color-border-primary, rgba(0,0,0,0.2))',
    background: 'var(--color-bg-primary, #fff)',
    color: 'var(--color-text-primary, #2C2C2A)',
    cursor: 'pointer',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 10,
    marginBottom: '1rem',
  },
  chartRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: 10,
    marginBottom: 10,
  },
  bottomRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 10,
  },
  stateWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '4rem 0',
  },
  stateText: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 13,
    color: 'var(--color-text-secondary, #5F5E5A)',
    margin: 0,
  },
  spinner: {
    width: 28,
    height: 28,
    border: '2px solid rgba(0,0,0,0.1)',
    borderTopColor: '#1D9E75',
    borderRadius: '50%',
    animation: 'skylight-spin 0.8s linear infinite',
  },
  retryBtn: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 12,
    padding: '6px 16px',
    borderRadius: 8,
    border: '0.5px solid rgba(0,0,0,0.2)',
    background: 'transparent',
    color: 'var(--color-text-primary, #2C2C2A)',
    cursor: 'pointer',
  },
};
